/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { Redirect } from 'react-router';

import ShareCardBottomBar from '../components/ShareCardBottomBar';
import ShareCardSummaryContent from './ShareCardSummaryContent';
import { NestedLanguageDropdownList } from '../../../components/LanguageDropdownList';
import ConnectedNestedIntlProvider from '../../../localization/ConnectedNestedIntlProvider';

import RouteNames from '../../../appAuthorized/RouteNames';

import SharePDFButton from './SharePDFButton';
import EditShareCardButton from './EditShareCardButton';
import { ReadOnlyShareCardHeaderTitle } from '../components/ShareCardHeaderTitle';

import ShareCardTemplate from '../components/ShareCardTemplate';
import { useShareCardForActiveProfile } from '../../../hooks/useShareCard';
import { useHealthEntriesForActiveProfile } from '../../../hooks/useHealthEntry';
import { useActiveProfile } from '../../../hooks/useProfile';
import { useActiveUser } from '../../../hooks/useUser';
import { useContactsForActiveProfile } from '../../../hooks/useContacts';
import { useNotesForActiveProfile } from '../../../hooks/useNote';
import { useFilesForActiveProfile } from '../../../hooks/useFile';
import { useLists } from '../../../hooks/useLists';
import { useNavigate } from '../../../hooks/useNavigate';
import { useUIState } from '../../../hooks/useUIState';

interface Props {
  shareCardId: number;
  editShareCard(): void;
}
const ShareCardSummary: React.FC<Props> = (props) => {
  const { shareCardId, editShareCard } = props;

  const [{ preferredLocale }] = useActiveUser();
  const [scState, scActions] = useShareCardForActiveProfile(shareCardId);

  const [{ groupedHealthEntryList }] = useHealthEntriesForActiveProfile();
  const [{ contactList }] = useContactsForActiveProfile();
  const [{ noteList }] = useNotesForActiveProfile();
  const [{ fileList }] = useFilesForActiveProfile();
  const [, profileActions] = useActiveProfile();
  const [{ localeList }] = useLists();
  const [, navActions] = useNavigate();
  const [, uiActions] = useUIState();

  const [localeState, setLocaleState] = React.useState(preferredLocale);

  const profile = profileActions.instantiateProfile();
  const languageCode = localeState.language_code;
  const shareCard = scActions.instantiate();
  const shareCardColorModel = scActions.getShareCardColor();

  if (!shareCard && scState.isLoaded) {
    return <Redirect to={RouteNames.share()} push={false} />;
  }

  return (
    <ShareCardTemplate
      shareCardColorModel={shareCardColorModel}
      disableScrolling={false}
      scrollAwareHeader={(
        <ReadOnlyShareCardHeaderTitle
          cssColor={shareCardColorModel.cssColor(1)}
          title={shareCard.title.get(languageCode)}
          iconClass={shareCard.iconClass}
          lightIcon={shareCard.lightIcon}
          onClose={() => {
            navActions.goBackTo(RouteNames.share());
          }}
          isSubmitDisabled={false}
          isGroupAccessible={shareCard.isGroupAccessible()}
        />
      )}
      shareCardFooter={(
        <ShareCardBottomBar>
          <SharePDFButton
            profileId={profile.data.id}
            isDependent={profile.isDependent()}
            shareCard={shareCard}
            languageCode={localeState.language_code}
          />
          <EditShareCardButton onClick={editShareCard} shareCard={shareCard} />


          <NestedLanguageDropdownList
            size="24px"
            htmlId={`sharecard-lang-${shareCard.data.id}`}
            value={localeState.id}
            onChange={(newLocale) => {
              uiActions.loadLocale(newLocale)
                .then(() => {
                  setLocaleState(localeList.filter(lang => lang.language_code === newLocale.language_code)[0] || preferredLocale);
                });
            }}
          />
        </ShareCardBottomBar>
      )}
      isContentLoaded={true}
      shareCardId={shareCard.data.id}
    >
      <ConnectedNestedIntlProvider locale={localeState}>
        <ShareCardSummaryContent
          shareCardColorModel={shareCardColorModel}
          shareCard={shareCard}
          healthEntries={groupedHealthEntryList}
          profile={profile}
          languageCode={localeState.language_code}
          onEnableUrl={() => {
            if (!shareCard.shareCardURL.isIncluded()) {
              shareCard.shareCardURL.toggle();
              scActions.apiUpdate(shareCard.data);
            }
          }}
          files={fileList}
          notes={noteList}
          contacts={contactList}
          appLevelLocale={preferredLocale}
        />
      </ConnectedNestedIntlProvider>
    </ShareCardTemplate>
  );
};

export default ShareCardSummary;
